/* =============================================================================
   CORE / STRIPE-CHECKOUT — geteilter Online-Bezahl-Flow (S234)
   =============================================================================
   Single-Source für die Stripe-Checkout-Integration der Gast-Kanäle
   (Takeaway + Lieferung, S234 T3). Vorher lebte der komplette Block nur in
   takeaway-form.js. Enthält:

     · stripeChannelEnabled(settings, channel)  — pure: Stripe global AN + Kanal
       togglebar über settings.stripeChannels (Default: takeaway + delivery)
     · buildStripeCheckoutBlockHtml({...})       — pure: Erfolgs-Insel-HTML
     · injectStripeCheckoutBlock({...})          — Insel in eine Success-Box hängen
     · startOrderStripeCheckout(orderId, opts)   — Edge-Call + Redirect (T1: merkt
       die Order lokal, damit Return-UX + Status-Seite den Bezahl-Link kennen)
     · Pending-Registry (localStorage): rememberPendingStripeOrder /
       getPendingStripeOrders / getPendingStripeOrder / clearPendingStripeOrder
     · handleStripeReturnFromUrl()               — ?stripe_return / ?stripe_cancel

   Guardrails: KEINE Server-Änderung hier. Die Zahlung selbst ist asynchron
   (Webhook); der Client zeigt nur Status/Return-UX. Order-IDs stehen NUR im
   localStorage des bestellenden Geräts, nie in URLs (T1-Vorgabe).
============================================================================ */

import { htmlEscape, showToast } from '../utils/helpers.js';

const PENDING_KEY = 'gg.stripe.pendingOrders';
const DEFAULT_STRIPE_CHANNELS = ['takeaway', 'delivery'];

function _getState() {
  return (typeof window !== 'undefined' && window.App && window.App.storage
    && window.App.storage._getInternalState && window.App.storage._getInternalState())
    || { settings: {} };
}

/**
 * Pure — ist Stripe für einen bestimmten Kanal aktiv? Stripe muss global in
 * `settings.paymentMethods` stehen UND der Kanal in `settings.stripeChannels`
 * (Default: alle, damit ein fehlendes Feld = beide Kanäle an — kein Regress
 * gegenüber „nur Takeaway"). channel: 'takeaway' | 'delivery' | 'customer'.
 */
export function stripeChannelEnabled(settings, channel) {
  const s = settings || {};
  const methods = Array.isArray(s.paymentMethods) ? s.paymentMethods : [];
  if (!methods.includes('stripe')) return false;
  const chans = Array.isArray(s.stripeChannels) ? s.stripeChannels : DEFAULT_STRIPE_CHANNELS;
  // 'customer' (Startseiten-Bestellung) auf 'takeaway' abbilden (gleicher Kanal).
  const key = channel === 'customer' ? 'takeaway' : channel;
  return chans.includes(key);
}

/**
 * S235-HF2 — Spec-Mirror der Stripe-Checkout-line_items-Berechnung. Diese pure
 * JS-Funktion IST die Wahrheit; die Edge `create-order-checkout-session` portiert
 * exakt diese Logik (Projekt-Muster „JS spiegelt Edge/SQL"). Sie stellt sicher,
 * dass die Liefergebühr (in `orders.delivery_address.fee_cents`, NICHT in
 * order_items) als eigenes line_item mitkassiert wird UND dass die Summe der
 * line_items exakt `total_gross_cents` trifft — sonst darf NIE ein abweichender
 * Betrag kassiert werden (Geldbetrags-Guard, S234-HF2-Befund: 18,70 statt 20,20 €).
 *
 * @param {object} p
 * @param {Array}  p.items       order_items ({name_snapshot, qty, unit_price_cents})
 * @param {number} p.feeCents    Liefergebühr in Cent (0 wenn keine / kein delivery)
 * @param {string} p.fallbackName Name für den Fallback (items leer)
 * @param {number} p.totalCents  orders.total_gross_cents (Soll-Summe)
 * @returns {{lineItems:Array<{name,qty,unit}>, sumCents:number, mismatch:boolean}}
 */
export function buildCheckoutLineItems({ items, feeCents, fallbackName, totalCents }) {
  const hasItems = Array.isArray(items) && items.length > 0;
  const base = hasItems
    ? items.map(it => ({
        name: String(it.name_snapshot || it.nameSnapshot || 'Position').slice(0, 250),
        qty:  Math.max(1, Number(it.qty) || 1),
        unit: Math.max(0, Math.round(Number(it.unit_price_cents != null ? it.unit_price_cents : it.unitPriceCents) || 0))
      }))
    : [{ name: String(fallbackName || 'Bestellung').slice(0, 250), qty: 1, unit: Math.max(0, Math.round(Number(totalCents) || 0)) }];
  // Liefergebühr NUR im Real-Items-Zweig anhängen (im Fallback ist totalCents
  // bereits inkl. Fee → kein Doppel).
  const fee = Math.max(0, Math.round(Number(feeCents) || 0));
  if (fee > 0 && hasItems) base.push({ name: 'Liefergebühr', qty: 1, unit: fee });
  const sumCents = base.reduce((s, r) => s + r.unit * r.qty, 0);
  const expected = Number(totalCents);
  const mismatch = Number.isFinite(expected) ? sumCents !== expected : false;
  return { lineItems: base, sumCents, mismatch };
}

// -----------------------------------------------------------------------------
// Pending-Registry (localStorage) — nur auf dem bestellenden Gerät
// -----------------------------------------------------------------------------

function _readPending() {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (_) { return []; }
}

function _writePending(arr) {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify((arr || []).slice(-20))); }
  catch (_) { /* Storage voll/blockiert — nicht-fatal */ }
}

/** Merkt eine unbezahlte, stripe-fähige Order lokal (id + Tracking-Token), damit
 *  Return-UX + Status-Seite den Bezahl-Link ohne Server-Rückfrage anbieten. */
export function rememberPendingStripeOrder(order) {
  if (!order || !order.id) return;
  const token = order.publicStatusToken || order.public_status_token || null;
  const entry = {
    orderId:     String(order.id),
    token:       token ? String(token) : null,
    orderNumber: String(order.orderNumber || order.order_number || ''),
    totalCents:  Number(order.totalCents || order.total_gross_cents || order.totalGrossCents || 0) || 0,
    ts:          (order._ts != null ? order._ts : null)   // Tests dürfen ts injizieren
  };
  const arr = _readPending().filter(e => e.orderId !== entry.orderId);
  arr.push(entry);
  _writePending(arr);
}

/** Alle gemerkten (noch offenen) Stripe-Orders dieses Geräts. */
export function getPendingStripeOrders() {
  return _readPending();
}

/** Gemerkte Order per Tracking-Token finden (für die Status-Seite). */
export function getPendingStripeOrderByToken(token) {
  if (!token) return null;
  return _readPending().find(e => e.token && e.token === String(token)) || null;
}

/** Zuletzt gemerkte Order (für ?stripe_return/?stripe_cancel ohne Kontext). */
export function getLatestPendingStripeOrder() {
  const arr = _readPending();
  return arr.length ? arr[arr.length - 1] : null;
}

/** Order aus der Registry entfernen (bezahlt / abgebrochen-verworfen). */
export function clearPendingStripeOrder(orderId) {
  if (!orderId) return;
  _writePending(_readPending().filter(e => e.orderId !== String(orderId)));
}

// -----------------------------------------------------------------------------
// Erfolgs-Insel (Takeaway + Lieferung teilen sie sich, S234 T3)
// -----------------------------------------------------------------------------

/** Pure — HTML der „💳 Direkt online bezahlen?"-Insel (ohne DOM). */
export function buildStripeCheckoutBlockHtml({ orderId, totalCents, blockId, btnId }) {
  const total = Number(totalCents || 0) / 100;
  const totalLabel = isFinite(total) && total > 0
    ? total.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
    : '';
  const safeId = String(orderId).replace(/'/g, "\\'");
  return `
    <div style="font-weight: 600; margin-bottom: 6px;">💳 Direkt online bezahlen?</div>
    <div style="font-size: 0.92rem; color: #4a5a6e; margin-bottom: 12px;">
      Du kannst deine Bestellung${totalLabel ? ' über ' + htmlEscape(totalLabel) : ''} jetzt sicher online via Stripe begleichen.
      Bei Abholung/Lieferung musst du dann nichts mehr bezahlen.
    </div>
    <button type="button" class="btn btn-primary" id="${htmlEscape(btnId)}"
            onclick="startStripeCheckout('${safeId}', '${htmlEscape(btnId)}')">
      Jetzt online bezahlen
    </button>`;
}

/**
 * Hängt die Stripe-Insel in eine Success-Box (Takeaway/Lieferung). Idempotent —
 * ein evtl. vorhandener Block wird zuerst entfernt. Merkt die Order zusätzlich in
 * der Pending-Registry (für Return-UX/Status-Seite), wenn Stripe für den Kanal an ist.
 * @param {object} p { successBox, order, channel, blockId, btnId }
 */
export function injectStripeCheckoutBlock({ successBox, order, channel, blockId, btnId }) {
  if (!successBox) return false;
  blockId = blockId || 'takeaway-stripe-block';
  btnId   = btnId   || 'takeaway-stripe-btn';
  const existing = successBox.querySelector('#' + blockId);
  if (existing) existing.remove();

  const settings = (_getState() && _getState().settings) || {};
  if (!stripeChannelEnabled(settings, channel)) return false;
  if (!order || !order.id) return false;

  // T1 — Order lokal merken (id + Tracking-Token), damit Return-UX + Status-Seite
  // den Bezahl-Link kennen (One-Shot-Problem behoben: Erfolgsbox weg ≠ Zugang weg).
  rememberPendingStripeOrder(order);

  const block = document.createElement('div');
  block.id = blockId;
  block.style.cssText = 'margin-top: 18px; padding: 14px; border-radius: 10px; background: #f0f7ff; border: 1px solid #b8d4f5; color: #16221c;';
  block.innerHTML = buildStripeCheckoutBlockHtml({
    orderId: order.id,
    totalCents: order.totalCents || order.total_gross_cents || order.totalGrossCents || 0,
    blockId, btnId
  });
  const lastBtn = successBox.querySelector('button:last-of-type');
  if (lastBtn) successBox.insertBefore(block, lastBtn);
  else successBox.appendChild(block);
  return true;
}

// -----------------------------------------------------------------------------
// Checkout-Start (Edge-Call + Redirect)
// -----------------------------------------------------------------------------

/**
 * Ruft die Edge Function `create-order-checkout-session` und redirected auf die
 * Stripe-Checkout-URL. `btnId` (optional) wird während des Calls deaktiviert.
 * Die Edge ist idempotent (prüft `payment_status='paid'` vorher) → ein erneuter
 * Klick auf einer bereits bezahlten Order chargt nicht doppelt.
 */
export async function startOrderStripeCheckout(orderId, btnId) {
  if (!orderId) return;
  const btn = btnId ? document.getElementById(btnId) : null;
  if (btn) { btn.disabled = true; btn.dataset._label = btn.textContent; btn.textContent = 'Verbinde mit Stripe…'; }

  const cfg = (typeof window !== 'undefined' && window.AppConfig && window.AppConfig.backend) || {};
  const apiBase = cfg.apiBaseUrl || '';
  const anonKey = (cfg.supabase && cfg.supabase.anonKey) || '';

  const _reset = () => { if (btn) { btn.disabled = false; btn.textContent = btn.dataset._label || 'Jetzt online bezahlen'; } };

  if (!apiBase || !anonKey) {
    showToast('Stripe-Setup unvollständig (apiBaseUrl/anonKey fehlt).', 'error');
    _reset();
    return;
  }

  const base = window.location.origin + window.location.pathname;
  const returnUrl = base + '?stripe_return=1';
  const cancelUrl = base + '?stripe_cancel=1';

  try {
    const resp = await fetch(apiBase.replace(/\/$/, '') + '/create-order-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': 'Bearer ' + anonKey,
        'apikey':        anonKey
      },
      body: JSON.stringify({ order_id: orderId, return_url: returnUrl, cancel_url: cancelUrl })
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || !data.url) {
      console.error('[startOrderStripeCheckout] Edge Function:', data);
      const errMsg = (data && (data.error || (data.stripe_error && data.stripe_error.message))) || ('HTTP ' + resp.status);
      showToast('Stripe-Aufruf fehlgeschlagen: ' + errMsg, 'error');
      _reset();
      return;
    }
    window.location.assign(data.url);
  } catch (err) {
    console.error('[startOrderStripeCheckout] Netzwerk-Fehler:', err);
    showToast('Netzwerk-Fehler — bitte später erneut versuchen.', 'error');
    _reset();
  }
}

// -----------------------------------------------------------------------------
// Return-UX (?stripe_return / ?stripe_cancel) — T1
// -----------------------------------------------------------------------------

/** Pure — welche Return-Aktion trägt die URL? 'return' | 'cancel' | null. */
export function parseStripeReturn(search) {
  const q = String(search || '');
  if (/[?&]stripe_return=1\b/.test(q)) return 'return';
  if (/[?&]stripe_cancel=1\b/.test(q)) return 'cancel';
  return null;
}

/** Entfernt die stripe_return/stripe_cancel-Query aus der URL (ohne Reload). */
function _stripReturnParam() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('stripe_return');
    url.searchParams.delete('stripe_cancel');
    url.searchParams.delete('session_id');
    history.replaceState(null, '', url.pathname + (url.search || '') + (url.hash || ''));
  } catch (_) { /* alte Browser — nicht-fatal */ }
}

/**
 * Boot-Handler (Gast-Kontext): wertet ?stripe_return / ?stripe_cancel aus.
 * - return: Erfolgs-Toast; die zuletzt gemerkte Order gilt als bezahlt → aus der
 *   Registry räumen; hat sie ein Tracking-Token, in die Live-Status-Seite routen.
 * - cancel: neutraler Hinweis + Retry-Angebot (Order bleibt gemerkt).
 * Räumt in beiden Fällen den Query-Param. Liefert die erkannte Aktion (oder null).
 */
export function handleStripeReturnFromUrl() {
  if (typeof window === 'undefined') return null;
  // S246 — auf der Tisch-Route gehört die Rückkehr der Tisch-Seite. Dort wurde
  // ein TEILBELEG bezahlt, nicht die zuletzt gemerkte Order dieses Geräts; der
  // Sprung nach `#track=` würde den Gast aus seinem Tisch werfen — und zwar in
  // die Bestellung von vorletzter Woche.
  if (/^#tisch\//.test(window.location.hash || '')) return null;
  const action = parseStripeReturn(window.location.search);
  if (!action) return null;
  const pending = getLatestPendingStripeOrder();

  if (action === 'return') {
    showToast('✅ Zahlung eingegangen — deine Bestellung ist bezahlt. Danke!', 'success');
    if (pending) {
      clearPendingStripeOrder(pending.orderId);
      // Hat die Order ein Tracking-Token → in die Live-Status-Seite routen
      // (zeigt den bestätigten Status; Polling holt „bezahlt" nach).
      if (pending.token && !/^#track=/.test(window.location.hash || '')) {
        _stripReturnParam();
        window.location.hash = '#track=' + encodeURIComponent(pending.token);
        if (typeof window.checkTrackingRequestFromUrl === 'function') {
          try { window.checkTrackingRequestFromUrl(); } catch (_) {}
        }
        return action;
      }
    }
    _stripReturnParam();
    return action;
  }

  // cancel
  showToast('Zahlung abgebrochen — du kannst bei Abholung/Lieferung bezahlen oder es erneut versuchen.', 'info');
  _stripReturnParam();
  return action;
}

// -----------------------------------------------------------------------------
// window-Bridge für inline-onclick (CSP-sauber: nur Funktionsnamen)
// -----------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  Object.assign(window, {
    startStripeCheckout: startOrderStripeCheckout,   // Backward-Compat-Name (Takeaway-Insel)
    startOrderStripeCheckout,
    handleStripeReturnFromUrl
  });
}
